/**
 * Small helpers for rendering serialized-component statuses + time-in-service.
 * Kept in one place so the badge colors stay consistent across the panel,
 * list, and detail page.
 */

export const STATUS_META = {
  new: {
    label: 'New',
    className: 'bg-sky-100 text-sky-800 border border-sky-200',
  },
  available: {
    label: 'Available',
    className: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  },
  installed: {
    label: 'Installed',
    className: 'bg-blue-600 text-white border border-blue-700',
  },
  awaiting_rebuild: {
    label: 'Awaiting Rebuild',
    className: 'bg-amber-100 text-amber-900 border border-amber-200',
  },
  decommissioned: {
    label: 'Decommissioned',
    className: 'bg-slate-200 text-slate-700 border border-slate-300',
  },
};

export const getStatusMeta = (status) => STATUS_META[status] || {
  label: status || 'Unknown',
  className: 'bg-slate-100 text-slate-700 border border-slate-200',
};

/**
 * Turn a total-seconds count into "60d 23h" or "3h 15m" or "just now".
 * Used both by the detail-page big-number and by inline event spans.
 */
export const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return 'just now';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

export const formatDurationLong = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return '0 days';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const parts = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (parts.length === 0) {
    const minutes = Math.floor(totalSeconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return parts.join(' ');
};

export const spanSecondsFromEvent = (ev) => {
  if (!ev?.installed_at) return 0;
  const start = new Date(ev.installed_at).getTime();
  const end = ev.removed_at ? new Date(ev.removed_at).getTime() : Date.now();
  return Math.max(0, Math.floor((end - start) / 1000));
};
